import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { db, collection, query, getDocs, addDoc, onSnapshot, doc, getDoc } from '../firebaseConfig';
import InitialCapitalSetupPopup from './InitialCapitalSetupPopup';

const CapitalManagement = ({ userID, trades, tradeAccountID }) => {
  const [loading, setLoading] = useState(true);
  const [capital, setCapital] = useState(0);
  const [deposits, setDeposits] = useState(0);
  const [withdrawals, setWithdrawals] = useState(0);
  const [totalReturns, setTotalReturns] = useState(0);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [showCapitalSetupPopup, setShowCapitalSetupPopup] = useState(false);
  const [accountName, setAccountName] = useState(tradeAccountID); // Default to ID
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  // Helper function to format numbers with commas
  const formatCurrency = (number) => {
    return number.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const modalStyles = {
    modalCap: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    },
    modalContentCap: {
      backgroundColor: theme.palette.background.default,
      padding: '20px',
      width: '400px',
      borderRadius: '14px',
    },
    closeButton: {
      padding: '0 16px',
      cursor: 'pointer',
      color: iconColor 
    },
    inputField: {
      padding: '8px',
      borderRadius: '4px',
      border: `1px solid ${theme.palette.divider}`,
      marginBottom: '1rem',
      width: '80%',
      color: iconColor,
    },
  };

  // Fetch account name on mount
  useEffect(() => {
    const fetchAccountName = async () => {
      if (tradeAccountID === 'default') {
        setAccountName('Default');
        return;
      }
      try {
        const accountDoc = await getDoc(doc(db, 'Users', userID, 'tradeAccounts', tradeAccountID));
        if (accountDoc.exists()) {
          setAccountName(accountDoc.data().name || tradeAccountID);
        }
      } catch (error) {
        console.error('Error fetching account name:', error);
      }
    };
    fetchAccountName();
  }, [userID, tradeAccountID]);
    
  useEffect(() => {
    if (!userID || !tradeAccountID) {
      setLoading(false);
      return;
    }
  
    const fetchCapitalData = async () => {
      try {
        console.log('\n=== CAPITAL MANAGEMENT DEBUG ===');
        console.log('UserID:', userID);
        console.log('TradeAccountID:', tradeAccountID);
        console.log('Trades passed:', trades);
  
        // Construct the Firestore path
        const capitalPath =
          tradeAccountID === 'default'
            ? `Users/${userID}/capital`
            : `Users/${userID}/tradeAccounts/${tradeAccountID}/capital`;
  
        // Calculate total returns from trades
        const calculateTotalReturns = () => {
          let totalTradeReturns = 0;
          const accountTrades = trades.filter(
            (trade) =>
              trade.accountID === tradeAccountID ||
              (!trade.accountID && tradeAccountID === 'default')
          );
          console.log('Filtered Trades for', tradeAccountID, ':', accountTrades);
  
          accountTrades
            .filter((entry) => entry.status === 'Closed')
            .forEach((entry) => {
              totalTradeReturns += Number(entry.returns) || 0;
            });
          console.log('Total Returns for', tradeAccountID, ':', totalTradeReturns);
          setTotalReturns(totalTradeReturns);
        };
  
        calculateTotalReturns();
  
        // Set up Firestore listener
        const capitalQuery = query(collection(db, capitalPath));
        const unsubscribe = onSnapshot(
          capitalQuery,
          (snapshot) => {
            console.log(
              'Snapshot received:',
              snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
            );
  
            let totalDeposits = 0;
            let totalWithdrawals = 0;
            let allTransactions = [];
  
            snapshot.forEach((doc) => {
              const data = doc.data();
              console.log('Processing doc:', doc.id, data);
  
              const currentDeposit = Number(data.deposit) || 0;
              const currentWithdrawal = Number(data.withdrawal) || 0;
  
              totalDeposits += currentDeposit;
              totalWithdrawals += currentWithdrawal;
  
              if ((currentDeposit || currentWithdrawal) && data.date) {
                const transactionDate = new Date(data.date);
                if (!isNaN(transactionDate.getTime())) {
                  allTransactions.push({
                    type: currentDeposit ? 'Deposit' : 'Withdrawal',
                    amount: currentDeposit || currentWithdrawal,
                    date: transactionDate,
                    id: doc.id,
                  });
                }
              }
            });
  
            console.log('Calculated:', {
              totalDeposits,
              totalWithdrawals,
              transactions: allTransactions,
            });
  
            // Update state
            setTransactions(allTransactions);
            setDeposits(totalDeposits);
            setWithdrawals(totalWithdrawals);
            const updatedCapital = totalDeposits - totalWithdrawals + totalReturns;
            setCapital(updatedCapital);
  
            // Delay popup check by 3 seconds
            setTimeout(() => {
              checkPopupCondition(
                updatedCapital,
                totalDeposits,
                totalWithdrawals,
                allTransactions
              );
            }, 3000); // 3-second delay
          },
          (error) => {
            console.error('Snapshot error:', error);
            setLoading(false);
          }
        );
  
        const checkPopupCondition = (
          currentCapital,
          currentDeposits,
          currentWithdrawals,
          currentTransactions
        ) => {
          console.log('Checking Popup Conditions:', {
            capital: currentCapital,
            deposits: currentDeposits,
            withdrawals: currentWithdrawals,
            transactionsLength: currentTransactions.length,
          });
  
          const hasNoActivity =
            currentDeposits === 0 &&
            currentWithdrawals === 0 &&
            currentTransactions.length === 0;
          const tutorialPopupShown = localStorage.getItem('tutorialOfferShown');
          const capitalSetupShown = sessionStorage.getItem('capitalSetupShown');
  
          if (hasNoActivity && tutorialPopupShown && !capitalSetupShown) {
            console.log('Showing Capital Setup Popup');
            setShowCapitalSetupPopup(true);
          } else {
            console.log('Popup not shown:', {
              hasNoActivity,
              tutorialPopupShown,
              capitalSetupShown,
            });
            if (!hasNoActivity && !capitalSetupShown) {
              console.log('Setting capitalSetupShown in sessionStorage');
              sessionStorage.setItem('capitalSetupShown', 'true');
            }
          }
        };
  
        setLoading(false);
  
        return () => unsubscribe();
      } catch (error) {
        console.error('Error in fetchCapitalData:', error);
        setLoading(false);
      }
    };
  
    fetchCapitalData();
  }, [userID, trades, tradeAccountID]);
  

  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount);
    if (!isNaN(amount) && tradeAccountID) {
      try {
        const capitalPath = tradeAccountID === 'default' 
          ? `Users/${userID}/capital` 
          : `Users/${userID}/tradeAccounts/${tradeAccountID}/capital`;
        const capitalSnapshot = await getDocs(query(collection(db, capitalPath)));
        const isFirstDeposit = capitalSnapshot.empty;
        
        if (isFirstDeposit && trades.length > 0) {
          let earliestTradeDate = new Date();
          trades.forEach((trade) => {
            const tradeDate = new Date(trade.date || trade.entryDate);
            if (tradeDate < earliestTradeDate) {
              earliestTradeDate = tradeDate;
            }
          });
          earliestTradeDate.setDate(earliestTradeDate.getDate() - 1);
          await addDoc(collection(db, capitalPath), {
            deposit: amount,
            date: earliestTradeDate.toISOString(),
            isInitialDeposit: true
          });
          alert("Initial deposit date set to before earliest trade.");
        } else {
          await addDoc(collection(db, capitalPath), {
            deposit: amount,
            date: new Date().toISOString()
          });
        }
  
        closeDepositModal();
      } catch (error) {
        console.error("Error handling deposit:", error);
        alert("Error processing deposit. Please try again.");
      }
    }
  };

  const handleWithdrawal = async () => {
    const amount = parseFloat(withdrawalAmount);
    if (!isNaN(amount) && tradeAccountID) {
      const capitalPath = tradeAccountID === 'default' 
        ? `Users/${userID}/capital` 
        : `Users/${userID}/tradeAccounts/${tradeAccountID}/capital`;
      await addDoc(collection(db, capitalPath), {
        withdrawal: amount,
        date: new Date().toISOString()
      });

      closeWithdrawalModal();
    }
  };

  const openDepositModal = () => setShowDepositModal(true);
  const closeDepositModal = () => {
    setShowDepositModal(false);
    setDepositAmount('');
  };
  const openWithdrawalModal = () => setShowWithdrawalModal(true);
  const closeWithdrawalModal = () => {
    setShowWithdrawalModal(false);
    setWithdrawalAmount('');
  };
  const closeCapitalSetupPopup = () => {
    setShowCapitalSetupPopup(false);
    sessionStorage.setItem('capitalSetupShown', 'true');
  };

  if (loading) return <div>Loading...</div>;

  const tooltipContent = `Capital Management Overview:\n\nTrack and manage your trading capital for this account, including deposits, withdrawals, real-time balance updates, profit/loss calculations, and transaction history.`;

  return (
    <Card sx={{ height: '100%', minHeight: '700px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <AccountBalanceWalletIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Capital Management {tradeAccountID === 'default' ? '' : `(${accountName})`}
            </MDTypography>
            <Tooltip 
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': {
                      color: theme.palette.background.paper,
                    }
                  }
                }
              }}
            >
              <InfoIcon 
                sx={{ 
                  ml: 1, 
                  fontSize: '1rem',
                  color: iconColor,
                  cursor: 'help'
                }} 
              />
            </Tooltip>
            <MDTypography variant="h4" fontFamily="inherit">{`$${formatCurrency(capital)}`}</MDTypography>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox p={2} sx={{ 
          flex: 1, 
          overflowY: 'auto',
          maxHeight: '400px',
          '&::-webkit-scrollbar': {
            width: '0.4em'
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.background.default
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.text.secondary,
            borderRadius: '4px'
          }
        }}>
          <MDTypography variant="h6" gutterBottom>
            Transaction History
          </MDTypography>
          {transactions.length === 0 ? (
            <MDTypography variant="body2" color="text">
              No transactions yet.
            </MDTypography>
          ) : (
            transactions.map((transaction) => (
              <MDBox 
                key={transaction.id} 
                display="flex" 
                justifyContent="space-between" 
                alignItems="center"
                mb={1}
                p={1}
                bgcolor={theme.palette.background.default}
                borderRadius="lg"
              >
                <MDBox>
                  <MDTypography variant="button" fontWeight="medium" color={transaction.type === 'Deposit' ? 'success' : 'error'}>
                    {transaction.type}
                  </MDTypography>
                  <MDTypography variant="caption" display="block" color="text">
                    {transaction.date.toLocaleDateString()} │ {transaction.date.toLocaleTimeString()}
                  </MDTypography>
                </MDBox>
                <MDTypography variant="button" fontWeight="bold" color={transaction.type === 'Deposit' ? 'success' : 'error'}>
                  {transaction.type === 'Deposit' ? '+' : '-'}${formatCurrency(transaction.amount)}
                </MDTypography>
              </MDBox>
            ))
          )}
        </MDBox>
        <MDBox pb={2} px={2}>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Total Deposits: <MDTypography component="span" variant="button" fontWeight="bold" color="success" fontFamily="inherit">{`$${formatCurrency(deposits)}`}</MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Total Withdrawals: <MDTypography component="span" variant="button" fontWeight="bold" color="error" fontFamily="inherit">{`$${formatCurrency(withdrawals)}`}</MDTypography>
          </MDTypography>
          <MDTypography component="p" variant="button" color="text" display="flex" fontFamily="inherit">
            Total Returns: <MDTypography component="span" variant="button" fontWeight="bold" color={totalReturns >= 0 ? "success" : "error"} fontFamily="inherit">{`$${formatCurrency(totalReturns)}`}</MDTypography>
          </MDTypography>
        </MDBox>
        <MDBox display="flex" justifyContent="center" pb={2} px={2}>
          <MDButton variant="contained" color="warning" onClick={openDepositModal} style={{ marginRight: '1rem' }}>
            Make Deposit
          </MDButton>
          <MDButton variant="contained" color="warning" onClick={openWithdrawalModal}>
            Make Withdrawal
          </MDButton>
        </MDBox>
      </MDBox>
      
      <InitialCapitalSetupPopup
        open={showCapitalSetupPopup}
        onClose={closeCapitalSetupPopup}
        onOpenDepositModal={openDepositModal}
      />
      {showDepositModal && (
        <div style={modalStyles.modalCap}>
          <div style={modalStyles.modalContentCap}>
            <MDTypography 
              component="span" 
              className="close" 
              onClick={closeDepositModal} 
              style={modalStyles.closeButton}
            >
              ×
            </MDTypography>
            <MDTypography variant="h5" align="center" gutterBottom>
              Deposit
            </MDTypography>
            <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" px={2} py={2}>
              <input
                type="number"
                placeholder="Enter deposit amount"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                style={modalStyles.inputField}
              />
              <MDButton variant="contained" color="warning" onClick={handleDeposit}>
                Confirm Deposit
              </MDButton>
            </MDBox>
          </div>
        </div>
      )}
      {showWithdrawalModal && (
        <div style={modalStyles.modalCap}>
          <div style={modalStyles.modalContentCap}>
            <MDTypography 
              component="span" 
              className="close" 
              onClick={closeWithdrawalModal} 
              style={modalStyles.closeButton}
            >
              ×
            </MDTypography>
            <MDTypography variant="h5" align="center" gutterBottom>
              Withdrawal
            </MDTypography>
            <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" px={2} py={2}>
              <input
                type="number"
                placeholder="Enter withdrawal amount"
                value={withdrawalAmount}
                onChange={(e) => setWithdrawalAmount(e.target.value)}
                style={modalStyles.inputField}
              />
              <MDButton variant="contained" color="warning" onClick={handleWithdrawal}>
                Confirm Withdrawal
              </MDButton>
            </MDBox>
          </div>
        </div>
      )}
    </Card>
  );
};

CapitalManagement.propTypes = {
  userID: PropTypes.string.isRequired,
  trades: PropTypes.array.isRequired,
  tradeAccountID: PropTypes.string.isRequired,
};

export default CapitalManagement;